"use client";
import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from "@/components/ui/table";
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "@/components/ui/select";
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from "@/components/ui/dialog";
import { Loader2, ArrowLeft, CheckCircle, XCircle, Clock, Undo2 } from "lucide-react";
import { useToast } from "@/components/ui/use-toast";
import { Link } from "react-router-dom";
import { format } from "date-fns";

const API_URL = import.meta.env.VITE_API_URL;

export default function ReturnDetailsPage() {
    const { id } = useParams();
    const navigate = useNavigate();
    const { toast } = useToast();

    const [returnData, setReturnData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [updating, setUpdating] = useState(false);
    const [statusDialogOpen, setStatusDialogOpen] = useState(false);
    const [newStatus, setNewStatus] = useState("");
    const [newRefundStatus, setNewRefundStatus] = useState("");
    const [statusNotes, setStatusNotes] = useState("");

    const fetchReturn = async () => {
        setLoading(true);
        try {
            const res = await axios.get(`${API_URL}purchases/returns/${id}`, {
                withCredentials: true,
            });
            setReturnData(res.data?.data);
            setNewStatus(res.data?.data?.status || "pending");
            setNewRefundStatus(res.data?.data?.refund_status || "pending");
        } catch (err) {
            console.error("Failed to fetch return:", err);
            toast({ variant: "destructive", title: "Error", description: "Failed to load return details" });
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (id) fetchReturn();
    }, [id]);

    const handleUpdateStatus = async () => {
        setUpdating(true);
        try {
            await axios.patch(`${API_URL}purchases/returns/${id}`, {
                status: newStatus,
                refund_status: newRefundStatus,
                notes: statusNotes,
            }, { withCredentials: true });

            toast({ title: "Success", description: "Return status updated" });
            setStatusDialogOpen(false);
            fetchReturn();
        } catch (err) {
            console.error("Failed to update return:", err);
            toast({ variant: "destructive", title: "Error", description: err.response?.data?.message || "Failed to update" });
        } finally {
            setUpdating(false);
        }
    };

    if (loading || !returnData) {
        return (
            <div className="flex items-center justify-center h-64">
                <Loader2 className="h-8 w-8 animate-spin" />
            </div>
        );
    }

    const statusColor = (status) => {
        switch (status) {
            case "completed": return "bg-green-100 text-green-800";
            case "approved": return "bg-blue-100 text-blue-800";
            case "pending": return "bg-yellow-100 text-yellow-800";
            case "rejected": return "bg-red-100 text-red-800";
            default: return "bg-gray-100 text-gray-800";
        }
    };

    const refundStatusColor = (status) => {
        switch (status) {
            case "refunded": return "bg-green-100 text-green-800";
            case "credited": return "bg-blue-100 text-blue-800";
            case "pending": return "bg-yellow-100 text-yellow-800";
            default: return "bg-gray-100 text-gray-800";
        }
    };

    const statusIcon = (status) => {
        switch (status) {
            case "completed": return <CheckCircle className="h-5 w-5 text-green-600" />;
            case "approved": return <CheckCircle className="h-5 w-5 text-blue-600" />;
            case "rejected": return <XCircle className="h-5 w-5 text-red-600" />;
            default: return <Clock className="h-5 w-5 text-yellow-600" />;
        }
    };

    return (
        <div className="mx-auto p-4 space-y-6">
            {/* Header */}
            <div className="flex flex-wrap items-center justify-between gap-4">
                <div className="flex items-center gap-4">
                    <Link to="/dashboard/purchases/returns-all/returns">
                        <Button variant="ghost" size="icon">
                            <ArrowLeft className="h-5 w-5" />
                        </Button>
                    </Link>
                    <div>
                        <h1 className="text-3xl font-bold">Return Details</h1>
                        <p className="text-muted-foreground font-mono">{returnData.return_number}</p>
                    </div>
                </div>
                <Button onClick={() => setStatusDialogOpen(true)}>
                    Update Status
                </Button>
            </div>

            {/* Summary Cards */}
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                <Card className="p-4">
                    <div className="flex items-center gap-3">
                        {statusIcon(returnData.status)}
                        <div>
                            <p className="text-sm text-muted-foreground">Status</p>
                            <Badge className={statusColor(returnData.status)} variant="outline">
                                {returnData.status?.toUpperCase()}
                            </Badge>
                        </div>
                    </div>
                </Card>
                <Card className="p-4">
                    <p className="text-sm text-muted-foreground">Refund Status</p>
                    <Badge className={refundStatusColor(returnData.refund_status)} variant="outline">
                        {returnData.refund_status?.toUpperCase() || "PENDING"}
                    </Badge>
                </Card>
                <Card className="p-4">
                    <p className="text-sm text-muted-foreground">Return Date</p>
                    <p className="text-lg font-semibold">
                        {format(new Date(returnData.return_date || returnData.createdAt), "dd MMM yyyy")}
                    </p>
                </Card>
                <Card className="p-4">
                    <p className="text-sm text-muted-foreground">Total Value</p>
                    <p className="text-lg font-bold">GHS {parseFloat(returnData.total || 0).toFixed(2)}</p>
                </Card>
            </div>

            {/* Purchase Info */}
            <Card>
                <CardHeader>
                    <CardTitle>Purchase Information</CardTitle>
                </CardHeader>
                <CardContent className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    <div>
                        <p className="text-sm text-muted-foreground">Purchase Invoice</p>
                        <Link to={`/dashboard/purchases/${returnData.purchase_id}`}>
                            <span className="font-medium text-blue-600 hover:underline">
                                {returnData.Purchase?.invoice_number || `PO-${returnData.purchase_id}`}
                            </span>
                        </Link>
                    </div>
                    <div>
                        <p className="text-sm text-muted-foreground">Supplier</p>
                        <p className="font-medium">{returnData.Purchase?.Supplier?.name || "N/A"}</p>
                    </div>
                    <div>
                        <p className="text-sm text-muted-foreground">Processed By</p>
                        <p className="font-medium">{returnData.processedBy?.username || "N/A"}</p>
                    </div>
                    <div>
                        <p className="text-sm text-muted-foreground">Reason</p>
                        <p className="font-medium">{returnData.reason || "-"}</p>
                    </div>
                </CardContent>
            </Card>

            {/* Returned Items */}
            <Card>
                <CardHeader>
                    <CardTitle>Returned Items</CardTitle>
                    <CardDescription>{returnData.items?.length || 0} items returned</CardDescription>
                </CardHeader>
                <CardContent>
                    <Table>
                        <TableHeader>
                            <TableRow>
                                <TableHead>Product</TableHead>
                                <TableHead>Quantity</TableHead>
                                <TableHead>Unit Cost</TableHead>
                                <TableHead>Reason</TableHead>
                                <TableHead className="text-right">Total</TableHead>
                            </TableRow>
                        </TableHeader>
                        <TableBody>
                            {(returnData.items || []).map((item, i) => (
                                <TableRow key={i}>
                                    <TableCell className="font-medium">
                                        {item.Product?.title || "Unknown Product"}
                                    </TableCell>
                                    <TableCell>{item.quantity}</TableCell>
                                    <TableCell>GHS {parseFloat(item.unit_cost || 0).toFixed(2)}</TableCell>
                                    <TableCell>
                                        <Badge variant="outline" className="capitalize">
                                            {item.reason?.replace("_", " ") || "N/A"}
                                        </Badge>
                                    </TableCell>
                                    <TableCell className="text-right font-medium">
                                        GHS {parseFloat(item.total || 0).toFixed(2)}
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </CardContent>
            </Card>

            {/* Notes */}
            {returnData.notes && (
                <Card>
                    <CardHeader>
                        <CardTitle>Notes</CardTitle>
                    </CardHeader>
                    <CardContent>
                        <p>{returnData.notes}</p>
                    </CardContent>
                </Card>
            )}

            {/* Status Update Dialog */}
            <Dialog open={statusDialogOpen} onOpenChange={setStatusDialogOpen}>
                <DialogContent>
                    <DialogHeader>
                        <DialogTitle>Update Return Status</DialogTitle>
                        <DialogDescription>
                            Change the status of this return
                        </DialogDescription>
                    </DialogHeader>
                    <div className="space-y-4">
                        <div>
                            <Label>Return Status</Label>
                            <Select value={newStatus} onValueChange={setNewStatus}>
                                <SelectTrigger>
                                    <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="pending">Pending</SelectItem>
                                    <SelectItem value="approved">Approved</SelectItem>
                                    <SelectItem value="completed">Completed</SelectItem>
                                    <SelectItem value="rejected">Rejected</SelectItem>
                                </SelectContent>
                            </Select>
                        </div>
                        <div>
                            <Label>Refund Status</Label>
                            <Select value={newRefundStatus} onValueChange={setNewRefundStatus}>
                                <SelectTrigger>
                                    <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="pending">Pending</SelectItem>
                                    <SelectItem value="refunded">Refunded</SelectItem>
                                    <SelectItem value="credited">Credited to Account</SelectItem>
                                </SelectContent>
                            </Select>
                        </div>
                        <div>
                            <Label>Notes (optional)</Label>
                            <Textarea
                                value={statusNotes}
                                onChange={(e) => setStatusNotes(e.target.value)}
                                placeholder="Add any notes about this status change..."
                            />
                        </div>
                    </div>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setStatusDialogOpen(false)}>
                            Cancel
                        </Button>
                        <Button onClick={handleUpdateStatus} disabled={updating}>
                            {updating ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
                            Update Status
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    );
}
